// The ONE node placard — shared by the Compass and the Mountain. A small glass
// card with a gold hairline in a fixed home (below the mountain base, centred,
// above the ▼ circle): kicker = sector · STAGE, title = what lives at that
// node, one state line, and "Explore ▼" to go down to the ORB screen.
import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { GlassFill } from './ui';
import { makeStyles } from './theme';

type Rung = { currentWindow: { count: number; needed: number; onTrack: boolean } };
type StageKey = 'E' | 'G' | 'A' | 'T' | 'S';

/**
 * Placard copy. Empty states INVITE (never grade); when something is set we show
 * its text + the ladder state, and the pace only when there is a real target
 * (needed > 0) to be behind on.
 */
export function placardCopy(stage: StageKey, sectorLabel: string, text: string | null | undefined, rung?: Rung | null): { text: string; state: string } {
  const set = (text ?? '').trim();
  if (!set) {
    const invite: Record<StageKey, string> = {
      E: 'Set your first Endeavour',
      G: `Set a Goal for ${sectorLabel}`,
      A: "Add this week's Aim",
      T: 'Nothing planned today — add a task',
      S: 'No step yet — pick one in KYRS',
    };
    return { text: invite[stage], state: '' };
  }
  const cw = rung?.currentWindow;
  if (!cw || !(cw.needed > 0)) return { text: set, state: '' };
  if (cw.count >= cw.needed) return { text: set, state: `ordered · ${cw.count}/${cw.needed}` };
  return { text: set, state: `${cw.count}/${cw.needed} · ${cw.onTrack ? 'on track' : 'behind pace'}` };
}

/** Gap kept free under the placard (the ▼ circle lives there). */
export const PLACARD_BOTTOM = 96;
export const PLACARD_H = 118;

// Dock: while a sector deity stands in the lower-left corner the placard rises above it (and hides
// under the deity's open chat box), so the two never cover each other. Shared by Compass + Mountain.
type Dock = { bottom: number; hidden: boolean };
let dock: Dock = { bottom: PLACARD_BOTTOM, hidden: false };
const dockSubs = new Set<(d: Dock) => void>();
export function setPlacardDock(next: Partial<Dock> | null): void {
  dock = next ? { ...dock, ...next } : { bottom: PLACARD_BOTTOM, hidden: false };
  dockSubs.forEach((fn) => fn(dock));
}
function usePlacardDock(): Dock {
  const [d, setD] = useState(dock);
  useEffect(() => {
    dockSubs.add(setD);
    setD(dock);
    return () => {
      dockSubs.delete(setD);
    };
  }, []);
  return d;
}

export function NodePlacard({ kicker, text, state, onExplore, tourRef }: { kicker: string; text: string; state: string; onExplore: () => void; /** Onboarding tour target ref for the placard card. */ tourRef?: (node: View | null) => void }) {
  const styles = useStyles();
  const { width } = useWindowDimensions();
  const w = Math.min(250, width - 24);
  const d = usePlacardDock();
  if (d.hidden) return null;
  return (
    <View ref={tourRef} style={[styles.card, { width: w, left: '50%', marginLeft: -w / 2, bottom: d.bottom, minHeight: PLACARD_H - 20 }]}>
      <GlassFill radius={14} tone="strong" />
      <Text style={styles.kick} numberOfLines={1}>
        {kicker}
      </Text>
      <Text style={styles.title} numberOfLines={2}>
        {text}
      </Text>
      {state ? (
        <Text style={styles.state} numberOfLines={2}>
          {state}
        </Text>
      ) : null}
      <Pressable onPress={onExplore} hitSlop={8} style={styles.go}>
        <Text style={styles.goText}>Explore ▼</Text>
      </Pressable>
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    card: { position: 'absolute', zIndex: 35, borderRadius: 14, borderWidth: 1, borderColor: t.gold, padding: 12, gap: 4, overflow: 'hidden', boxShadow: `0 8px 24px ${t.glowSoft}` } as object,
    kick: { fontSize: 11, fontWeight: '800', letterSpacing: 1.6, color: t.goldBright, textTransform: 'uppercase' },
    title: { fontSize: 16, fontWeight: '800', color: t.ivory, lineHeight: 20 },
    state: { fontSize: 12, color: t.ivorySoft },
    go: { alignSelf: 'flex-end', marginTop: 2, outlineStyle: 'none' } as object,
    goText: { fontSize: 13, fontWeight: '800', color: t.goldBright },
  }),
);
